import { useEffect, useRef, useState } from "react";
import { createSolarScene } from "@/lib/solar-scenes";
import { fallbackFor } from "@/lib/fallbacks";

/**
 * Lazy, lifecycle-safe 3D scene. Mounts WebGL only when near the viewport and
 * releases the context when scrolled far away, so the browser never runs out
 * of contexts. Shows a polished photo fallback when WebGL is unavailable,
 * fails to start, or the canvas loses its context (?nogl=1 simulates this).
 */
export default function SolarScene({ kind = "commercial", className = "", state = {}, fallbackAlt, testId }) {
  const hostRef = useRef(null);
  const apiRef = useRef(null);
  const kindRef = useRef(kind);
  const unbindRef = useRef(null);
  const [active, setActive] = useState(false);
  const [failed, setFailed] = useState(false);
  const stateKey = JSON.stringify(state);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const io = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), { rootMargin: "300px 0px" });
    io.observe(host);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("nogl")) setFailed(true);
  }, []);

  useEffect(() => {
    if (!active || failed || apiRef.current || !hostRef.current) return;
    try {
      apiRef.current = createSolarScene(hostRef.current, kindRef.current, state || {});
      const canvas = hostRef.current.querySelector("canvas");
      const onLost = (event) => {
        event.preventDefault();
        setFailed(true);
      };
      canvas?.addEventListener("webglcontextlost", onLost);
      unbindRef.current = () => canvas?.removeEventListener("webglcontextlost", onLost);
    } catch (error) {
      setFailed(true);
    }
  }, [active, failed, stateKey]);

  useEffect(() => {
    if (active && apiRef.current) {
      const next = { ...state };
      if (kind !== kindRef.current) {
        next.kind = kind;
        kindRef.current = kind;
        apiRef.current.update(next);
        return;
      }
      apiRef.current.update(next);
    } else {
      kindRef.current = kind;
    }
  }, [kind, stateKey, active]);

  // release the WebGL context when scrolled far offscreen
  useEffect(() => {
    if (!active && apiRef.current) {
      apiRef.current.dispose();
      apiRef.current = null;
      if (unbindRef.current) unbindRef.current();
    }
  }, [active]);

  useEffect(
    () => () => {
      if (apiRef.current) apiRef.current.dispose();
      apiRef.current = null;
      if (unbindRef.current) unbindRef.current();
    },
    [],
  );

  return (
    <div ref={hostRef} className={`scene ${className}`} data-testid={testId || `scene-${kind}`}>
      {failed && (
        <img
          src={fallbackFor(kind)}
          alt={fallbackAlt || "Representative photo of a completed solar installation"}
          className="scene-fallback"
          data-testid="scene-fallback"
          loading="lazy"
        />
      )}
    </div>
  );
}
